import { green } from '@ant-design/colors';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  InsertRowBelowOutlined,
  PlusOutlined,
  SettingOutlined,
  TableOutlined
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tooltip,
  Typography,
  Upload,
  message
} from 'antd';
import { cloneDeep } from 'lodash';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { SubSubtitleStart } from '../../components';
import CurrencyInputCustom from '../../components/CurrencyInputCustom';
import NotFoundContentLoading from '../../components/NotFoundContentLoading';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import { TIPOS_VEHICULO } from '../../constants';
import {
  currencyToNumber,
  errorHandler,
  getCriterio,
  getDescripcionOpcion
} from '../../utils';
import { instanceRef } from '../../utils/axios';
import { formatoMonedaGenerico } from '../../utils/converters';

const { TabPane } = Tabs;
const { Option } = Select;

const formatosAceptados =
  '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';

const renderPorcentaje = (value) =>
  value ? <span style={{ whiteSpace: 'nowrap' }}>{`${value} %`}</span> : '';

const getCoberturaSebaot = (id, coberturasSistemaCore) => {
  const coberturaSistemaCore = coberturasSistemaCore.find(
    (item) => item.codigo_cobertura === id
  );
  if (coberturaSistemaCore) {
    return coberturaSistemaCore;
  }
  return null;
};

const coberturaAdapter = (values, moneda, coberturasSistemaCore) => {
  const adapt = { ...values };
  if (values.tasa_tipo === 'tasa_pura') {
    adapt.tasa_config = null;
  }
  adapt.cobertura_config = getCoberturaSebaot(
    values.codigo_cobertura,
    coberturasSistemaCore
  );
  adapt.costo_max = adapt.costo_max
    ? currencyToNumber(adapt.costo_max, moneda)
    : null;
  adapt.costo_min = adapt.costo_min
    ? currencyToNumber(adapt.costo_min, moneda)
    : null;

  if (values.opcion === 'monto_fijo') {
    adapt.monto = adapt.monto ? currencyToNumber(adapt.monto, moneda) : null;
  }
  return adapt;
};

const CoberturaOpcionForm = ({
  form,
  formName,
  aseguradoraProducto,
  monedaList,
  loadingCobertura,
  coberturasSistemaCore,
  onFinish
}) => {
  return (
    <Form
      form={form}
      onFinish={onFinish}
      scrollToFirstError
      name={formName}
      autoComplete="off"
      layout="vertical"
    >
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue, setFieldsValue }) => {
          return (
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item name="id" hidden />
                <Form.Item
                  label="Moneda"
                  name="id_moneda"
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Space direction="vertical">
                      {monedaList?.map((item) => (
                        <Radio key={item.id} value={item.id}>
                          {`${item.descripcion} (${item.simbolo})`}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  label="Opción"
                  name="opcion"
                  rules={[{ required: true }]}
                  onChange={() =>
                    setFieldsValue({
                      criterio: null,
                      monto: null
                    })
                  }
                >
                  <Radio.Group>
                    <Space direction="vertical">
                      <Radio value="valor_capital">Valor capital</Radio>
                      <Radio value="porcentaje_capital">
                        Porcentaje capital
                      </Radio>
                      <Radio value="monto_fijo">Monto fijo</Radio>
                      {aseguradoraProducto?.producto?.codigo === 1 && (
                        <>
                          <Radio value="suma_tabla_ovp">Suma tabla OVP</Radio>
                          <Radio value="suma_tabla_rc">Suma tabla RC</Radio>
                        </>
                      )}
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    if (getFieldValue('opcion') === 'porcentaje_capital') {
                      return (
                        <Form.Item
                          label="Porcentaje"
                          name="criterio"
                          rules={[{ required: true }]}
                        >
                          <Input
                            style={{
                              width: 200
                            }}
                            placeholder="0"
                            min={0}
                            max={100}
                            step="any"
                            type="number"
                            suffix="%"
                          />
                        </Form.Item>
                      );
                    }
                    if (getFieldValue('opcion') === 'monto_fijo') {
                      return (
                        <Form.Item
                          label="Monto"
                          name="monto"
                          rules={[{ required: true }]}
                        >
                          <CurrencyInputCustom
                            moneda={monedaList.find(
                              (e) => e.id === form.getFieldValue('id_moneda')
                            )}
                            bordered
                            style={{ height: 35.14 }}
                          />
                        </Form.Item>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    if (getFieldValue('opcion') !== 'monto_fijo') {
                      return (
                        <Form.Item
                          label="Tipo de tasa"
                          name="tasa_tipo"
                          rules={[
                            { required: true },
                            () => ({
                              validator(_, value) {
                                const tasaConfig = getFieldValue('tasa_config');
                                const tieneErrores = tasaConfig
                                  ? Object.keys(tasaConfig).some((key) =>
                                      errorPorTipo(
                                        tasaConfig[key],
                                        monedaList.find(
                                          (e) =>
                                            e.id ===
                                            form.getFieldValue('id_moneda')
                                        )
                                      )
                                    )
                                  : true;
                                if (value === 'tasa_config' && tieneErrores) {
                                  return Promise.reject(
                                    new Error('Debe configurar las tasas')
                                  );
                                }
                                return Promise.resolve();
                              }
                            })
                          ]}
                        >
                          <Radio.Group>
                            <Space direction="vertical">
                              <Radio value="tasa_pura">Tasa pura</Radio>
                              {aseguradoraProducto?.producto?.codigo === 1 && (
                                <Radio value="tasa_config">
                                  Tabla de tasas
                                </Radio>
                              )}
                            </Space>
                          </Radio.Group>
                        </Form.Item>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    if (getFieldValue('opcion') !== 'monto_fijo') {
                      if (getFieldValue('tasa_tipo') === 'tasa_pura') {
                        return (
                          <Form.Item
                            label="Tasa pura"
                            name="tasa_pura"
                            rules={[{ required: true }]}
                          >
                            <Input
                              style={{
                                width: 200
                              }}
                              placeholder="0"
                              min={0}
                              max={100}
                              step="any"
                              type="number"
                              suffix="%"
                            />
                          </Form.Item>
                        );
                      }
                      if (getFieldValue('tasa_tipo') === 'tasa_config') {
                        return (
                          <>
                            <Form.Item
                              label="Tasa de configuraciones"
                              name="tasa_config"
                              noStyle
                            >
                              <Button
                                type="primary"
                                ghost
                                htmlType="button"
                                icon={<TableOutlined />}
                                // onClick={() => setFormTasaVisible(true)}
                              >
                                Configurar tasas
                              </Button>
                            </Form.Item>
                            <br />
                            <br />
                          </>
                        );
                      }
                    }

                    return null;
                  }}
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Costo Mínimo"
                  name="costo_min"
                  dependencies={['costo_max']}
                  rules={[
                    () => ({
                      validator(_, value) {
                        const max = getFieldValue('costo_max');
                        if (
                          minMaxValidator(
                            value,
                            max,
                            monedaList.find(
                              (e) => e.id === form.getFieldValue('id_moneda')
                            )
                          )
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            'El monto máximo no puede ser menor al mínimo'
                          )
                        );
                      }
                    })
                  ]}
                  normalize={(value) =>
                    currencyToNumber(
                      value,
                      monedaList.find(
                        (e) => e.id === form.getFieldValue('id_moneda')
                      )
                    )
                  }
                >
                  <CurrencyInputCustom
                    moneda={monedaList.find(
                      (e) => e.id === form.getFieldValue('id_moneda')
                    )}
                    bordered
                    style={{ height: 35.14 }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Costo Máximo"
                  name="costo_max"
                  normalize={(value) =>
                    currencyToNumber(
                      value,
                      monedaList.find(
                        (e) => e.id === form.getFieldValue('id_moneda')
                      )
                    )
                  }
                >
                  <CurrencyInputCustom
                    moneda={monedaList.find(
                      (e) => e.id === form.getFieldValue('id_moneda')
                    )}
                    bordered
                    style={{ height: 35.14 }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Es una cobertura secundaria"
                  name="secundaria"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Si" unCheckedChildren="No" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Aplica fórmula"
                  name="aplica_formula"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Si" unCheckedChildren="No" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Aplica descuento/recargo"
                  name="aplica_descuento"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Si" unCheckedChildren="No" />
                </Form.Item>
              </Col>
              <Col xs={24} md={24}>
                <Form.Item
                  label="Cobertura Sistema Core"
                  name="codigo_cobertura"
                >
                  <Select
                    loading={loadingCobertura}
                    notFoundContent={
                      <NotFoundContentLoading loading={loadingCobertura} />
                    }
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    optionFilterProp="children"
                    placeholder="Seleccionar cobertura"
                  >
                    {coberturasSistemaCore.map((item) => (
                      <Option
                        key={`${item.codigo_cobertura}`}
                        value={item.codigo_cobertura}
                      >
                        {`${item.codigo_cobertura} - ${item.nombre_cobertura}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          );
        }}
      </Form.Item>
    </Form>
  );
};

const currencyRender = (value, moneda) => {
  if (!moneda) {
    return '';
  }
  return value ? formatoMonedaGenerico(moneda, value) : '';
};

const AseguradoraProductoCoberturaOpciones = ({
  opciones,
  monedas,
  loading,
  onAdd,
  onEdit,
  onDelete,
  coberturasSistemaCore
}) => {
  const data = opciones;
  const columns = [
    {
      title: 'Tipo',
      dataIndex: ['opcion'],
      key: ['opcion'],
      render: (opcion) => {
        return getDescripcionOpcion(opcion);
      }
    },
    {
      title: 'Valor',
      dataIndex: ['valor'],
      key: ['valor'],
      align: 'right',
      render: (_, record) => {
        if (!record.id_moneda || !coberturasSistemaCore) {
          return '';
        }
        const adapt = coberturaAdapter(
          record,
          monedas.find((e) => e.id === record.id_moneda),
          coberturasSistemaCore
        );
        return getCriterio(
          adapt,
          monedas.find((e) => e.id === record.id_moneda)
        );
      }
    },
    {
      title: 'Tasa pura',
      dataIndex: ['tasa_pura'],
      key: ['tasa_pura'],
      align: 'right',
      render: renderPorcentaje
    },
    {
      title: 'Costo mínimo',
      dataIndex: ['costo_min'],
      key: ['costo_min'],
      align: 'right',
      render: (value, record) => {
        return currencyRender(
          value,
          monedas.find((e) => e.id === record.id_moneda)
        );
      }
    },
    {
      title: 'Costo máximo',
      dataIndex: ['costo_max'],
      key: ['costo_max'],
      align: 'right',
      render: (value, record) => {
        return currencyRender(
          value,
          monedas.find((e) => e.id === record.id_moneda)
        );
      }
    },
    {
      title: 'Secundaria',
      dataIndex: ['secundaria'],
      key: ['secundaria'],
      render: (value) => (value ? 'Si' : 'No')
    },
    {
      title: 'Aplica fórmula',
      dataIndex: ['aplica_formula'],
      key: ['aplica_formula'],
      render: (value) => (value ? 'Si' : 'No')
    },
    {
      title: 'Descuento/Recargo',
      dataIndex: ['aplica_descuento'],
      key: ['aplica_descuento'],
      render: (value) => (value ? 'Si' : 'No')
    },
    {
      title: 'Acciones',
      key: 'action',
      width: '10px',
      align: 'left',
      render: (_, record, index) => {
        return (
          <Space size="small">
            <Tooltip title="Editar">
              <Button
                icon={<EditOutlined />}
                type="text"
                size="middle"
                onClick={() => onEdit(record)}
              />
            </Tooltip>
            <Tooltip title="Eliminar">
              <Button
                icon={<DeleteOutlined />}
                type="text"
                size="middle"
                onClick={() => onDelete(index)}
              />
            </Tooltip>
          </Space>
        );
      }
    }
  ];
  return (
    <>
      <Space size="large">
        <SubSubtitleStart>Opciones disponibles</SubSubtitleStart>
        <Button
          type="primary"
          ghost
          icon={<PlusOutlined />}
          size="small"
          onClick={onAdd}
        >
          Agregar
        </Button>
      </Space>

      <br />
      <Table
        loading={loading}
        scroll
        style={{ width: 'fit-content' }}
        bordered
        dataSource={data}
        columns={columns}
        pagination={false}
        rowKey={(record) => record.id}
        size="small"
      />
    </>
  );
};

const minMaxValidator = (min, max, moneda) => {
  return !(
    min &&
    max &&
    currencyToNumber(min, moneda) > currencyToNumber(max, moneda)
  );
};

const errorMinimo = (record, listado, moneda) => {
  const actual = listado.find((element) => element.id === record.id);
  const anterior = listado.find((element) => element.id === record.id - 1);

  if (anterior) {
    return (
      currencyToNumber(actual.monto_min, moneda) -
        currencyToNumber(anterior.monto_max, moneda) !==
      1
    );
  }
  return false;
};

const errorMaximo = (record, listado, moneda) => {
  const actual = listado.find((element) => element.id === record.id);
  const siguiente = listado.find((element) => element.id === record.id + 1);

  if (siguiente) {
    return (
      currencyToNumber(siguiente.monto_min, moneda) -
        currencyToNumber(actual.monto_max, moneda) !==
      1
    );
  }
  return false;
};

const errorPorTipo = (listado, moneda) => {
  const errorRango = listado.some((item) => {
    return (
      errorMinimo(item, listado, moneda) || errorMaximo(item, listado, moneda)
    );
  });
  const errorPorcentajes = listado.some((item) => {
    return (
      Number(item.origen.nuevo) === 0 ||
      Number(item.origen.representante) === 0 ||
      Number(item.origen.volantecambiado) === 0 ||
      Number(item.origen.importacion) === 0
    );
  });
  return errorRango || errorPorcentajes;
};

const AseguradoraProductoCoberturaForm = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [aseguradoraProducto, setAseguradoraProducto] = useState();
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [loadingCobertura, setLoadingCobertura] = useState(false);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [coberturasSistemaCore, setCoberturasSistemaCore] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [tipoActual, setTipoActual] = useState('AUTOMOVIL');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalOpcionVisible, setModalOpcionVisible] = useState(false);
  const [insertarIntermedio, setInsertarIntermedio] = useState(false);
  const [idActual, setIdActual] = useState(false);
  const [formTasaVisible, setFormTasaVisible] = useState(false);
  const [coberturaConfigurada, setCoberturaConfigurada] = useState();
  const [opcionesCobertura, setOpcionesCobertura] = useState([]);
  const [moneda, setMoneda] = useState();
  const [monedas, setMonedas] = useState();
  const [ultimoId, setUltimoId] = useState(-1);

  const {
    params: { id: idAseguradoraProducto, idCobertura }
  } = useRouteMatch();
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [modalOpcion] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState({});

  const configInicial = useMemo(() => {
    const config = {};
    TIPOS_VEHICULO.forEach((item) => {
      config[item] = [
        {
          id: 1,
          tipo: item,
          monto_min: 1,
          monto_max: null,
          origen: {
            nuevo: 0,
            representante: 0,
            volantecambiado: 0,
            importacion: 0
          }
        }
      ];
    });
    return config;
  }, []);

  const getMonedas = async () => {
    try {
      const monedasResponse = await instanceRef.instance.get(`/moneda`);
      if (monedasResponse.data?.data) {
        setMonedas(monedasResponse.data?.data);
      }
    } catch (error) {
      errorHandler(error, 'No se pudo obtener monedas');
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setEliminando(true);
      await instanceRef.instance.delete(
        `/cobertura/${coberturaConfigurada.id}`
      );
      setEliminando(false);
      history.push(`/aseguradoraproducto/cobertura/${idAseguradoraProducto}`);
    } catch (err) {
      errorHandler(err, 'Error al eliminar');
      setEliminando(false);
    }
  };

  const formatearListado = (tasaConfigParam) => {
    const tasaConfig = { ...tasaConfigParam };
    if (tasaConfig[tipoActual].length === 0) {
      tasaConfig[tipoActual] = [
        {
          id: 1,
          tipo: tipoActual,
          monto_min: 1,
          monto_max: null,
          origen: {
            nuevo: 0,
            representante: 0,
            volantecambiado: 0,
            importacion: 0
          }
        }
      ];
    } else {
      const primero = tasaConfig[tipoActual][0];
      // Si el primer elemento (luego de borrar todo) no inicia en 1, se agrega el rango inicial
      if (currencyToNumber(primero.monto_min, moneda) > 1) {
        tasaConfig[tipoActual].unshift({
          id: 1,
          tipo: tipoActual,
          monto_min: 1,
          monto_max: currencyToNumber(primero.monto_min, moneda) - 1,
          origen: {
            nuevo: 0,
            representante: 0,
            volantecambiado: 0,
            importacion: 0
          }
        });
      }
      const ultimo = tasaConfig[tipoActual][tasaConfig[tipoActual].length - 1];
      // Si el ultimo elemento (luego de borrar todo) no llega hasta infinito, se agrega el rango final
      if (
        ultimo.monto_max != null &&
        currencyToNumber(ultimo.monto_max, moneda) !== 0
      ) {
        tasaConfig[tipoActual].push({
          id: tasaConfig[tipoActual].length + 1,
          tipo: tipoActual,
          monto_min: currencyToNumber(ultimo.monto_max, moneda) + 1,
          monto_max: null,
          origen: {
            nuevo: 0,
            representante: 0,
            volantecambiado: 0,
            importacion: 0
          }
        });
      }
    }

    tasaConfig[tipoActual].forEach((_, index) => {
      tasaConfig[tipoActual][index].id = index + 1;
    });

    return tasaConfig;
  };

  const onSelectChange = (newSelectedRowKeys) => {
    selectedRowKeys[tipoActual] = newSelectedRowKeys;
    setSelectedRowKeys({ ...selectedRowKeys });
  };
  const rowSelection = {
    selectedRowKeys: selectedRowKeys[tipoActual],
    onChange: onSelectChange
  };
  const hasSelected = selectedRowKeys[tipoActual]?.length > 0;

  const loadAseguradoraProducto = async () => {
    try {
      setLoading(true);

      const response = await instanceRef.instance.get(
        `aseguradoraproducto/id/${idAseguradoraProducto}`
      );
      const data = response.data?.data;
      if (data) {
        setAseguradoraProducto(data);
      }

      setLoading(false);
    } catch (error) {
      errorHandler(error);
      setLoading(false);
    }
  };

  const loadModel = async () => {
    try {
      setLoading(true);
      const response = await instanceRef.instance.get(
        `aseguradoraproducto/coberturas/${idAseguradoraProducto}/${idCobertura}`
      );
      const data = response.data?.data;
      if (data) {
        setCoberturaConfigurada(data);
        setOpcionesCobertura(
          data.subcoberturas.map((item) => ({
            ...item,
            codigo_cobertura: item.cobertura_config
              ? item.cobertura_config.codigo_cobertura
              : null
          }))
        );
      } else {
        setCoberturaConfigurada(null);
      }

      setLoading(false);
    } catch (error) {
      errorHandler(error);
      setLoading(false);
    }
  };

  const loadCoberturasSebaot = async () => {
    try {
      setLoadingCobertura(true);

      const response = await instanceRef.instance.get(
        `sebaot/aseguradoraproducto/${idAseguradoraProducto}/coberturas`
      );
      const data = response.data?.data;
      if (data) {
        setCoberturasSistemaCore(data);
      }

      setLoadingCobertura(false);
    } catch (error) {
      errorHandler(error);
      setLoadingCobertura(false);
    }
  };

  const loadGrupos = async () => {
    try {
      setLoadingGrupos(true);

      const response = await instanceRef.instance.get(`cobertura/grupos`);
      const data = response.data?.data;
      if (data) {
        setGrupos(data);
      }

      setLoadingGrupos(false);
    } catch (error) {
      errorHandler(error);
      setLoadingGrupos(false);
    }
  };

  const uploadFile = async (file, setter, loadingFile) => {
    try {
      loadingFile(true);
      const formData = new FormData();
      formData.append('file', file.file);
      const response = await instanceRef.instance.post(
        `archivos/tasas`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );
      setter(response.data.data);
      loadingFile(false);
    } catch (error) {
      if (error?.response?.data?.message) {
        Modal.error({
          maskClosable: true,
          closable: true,
          width: 1000,
          title: error?.response?.data?.message.mensaje,
          content: (
            <>
              {error?.response?.data.message.errorList.map((item, index) => (
                <Fragment key={index}>
                  <span>
                    ID: {item.id}, Error: {item.error}
                  </span>
                  <br />
                </Fragment>
              ))}
            </>
          )
        });
      } else {
        message.error('Verificar el formato del archivo');
      }
      loadingFile(false);
    }
  };

  const onChangeActiveTab = (tab) => {
    setTipoActual(tab);
  };

  const onOk = () => {
    const tasaConfig = form.getFieldValue('tasa_config');
    const tieneErrores = Object.keys(tasaConfig).some((key) =>
      errorPorTipo(tasaConfig[key], moneda)
    );
    if (tieneErrores) {
      message.error('Verificar rangos de todos los tipos');
    } else {
      setFormTasaVisible(false);
    }
  };

  const onCancel = () => {
    setFormTasaVisible(false);
  };

  const normalizePorcentaje = (value) => {
    if (Number(value) > 100) {
      return 100;
    }
    if (Number(value) < 0) {
      return 0;
    }
    return Number(value);
  };

  const agregarFila = (record, listado) => {
    setModalVisible(true);
    setInsertarIntermedio(true);
    setIdActual(record.id);
    const anterior = listado.find((element) => element.id === record.id);
    const siguiente = listado.find((element) => element.id === record.id + 1);
    if (!siguiente) {
      modalForm.setFieldsValue({
        // id: idNuevo,
        tipo: tipoActual,
        monto_min: currencyToNumber(anterior.monto_min, moneda) + 1,
        monto_max: 0,
        origen: {
          nuevo: 0,
          representante: 0,
          volantecambiado: 0,
          importacion: 0
        }
      });
    } else {
      const diferencia =
        currencyToNumber(siguiente.monto_min, moneda) -
        currencyToNumber(anterior.monto_max, moneda);
      if (diferencia > 1) {
        modalForm.setFieldsValue({
          // id: idNuevo,
          tipo: tipoActual,
          monto_min: currencyToNumber(anterior.monto_max, moneda) + 1,
          monto_max: currencyToNumber(siguiente.monto_min, moneda) - 1,
          origen: {
            nuevo: 0,
            representante: 0,
            volantecambiado: 0,
            importacion: 0
          }
        });
      } else {
        modalForm.setFieldsValue({
          // id: idNuevo,
          tipo: tipoActual,
          monto_min: currencyToNumber(anterior.monto_max, moneda) + 1,
          monto_max: currencyToNumber(anterior.monto_max, moneda) + 1,
          origen: {
            nuevo: 0,
            representante: 0,
            volantecambiado: 0,
            importacion: 0
          }
        });
      }
    }
  };

  const onFinishRango = (values) => {
    const tasaConfig = form.getFieldValue('tasa_config');

    const idNuevo = tasaConfig[tipoActual].length + 1;
    const indexRangoModificado = tasaConfig[tipoActual].findIndex(
      (item) => item.id === idActual
    );
    if (insertarIntermedio) {
      tasaConfig[tipoActual].splice(indexRangoModificado + 1, 0, {
        tipo: tipoActual,
        ...values,
        monto_min: currencyToNumber(values.monto_min, moneda),
        monto_max: currencyToNumber(values.monto_max, moneda)
      });
      tasaConfig[tipoActual].forEach((_, index) => {
        tasaConfig[tipoActual][index].id = index + 1;
      });
    } else if (indexRangoModificado === tasaConfig[tipoActual].length - 1) {
      // Si se está editando y es el último registro
      tasaConfig[tipoActual][indexRangoModificado] = {
        ...tasaConfig[tipoActual][indexRangoModificado],
        tipo: tipoActual,
        ...values,
        monto_min: currencyToNumber(values.monto_min, moneda),
        monto_max: currencyToNumber(values.monto_max, moneda)
      };
      // Si el monto_max está definido, se inserta una nueva fila automáticamente para cubrir hasta infinito
      if (
        values.monto_max != null &&
        currencyToNumber(values.monto_max, moneda) !== 0
      ) {
        tasaConfig[tipoActual] = [
          ...tasaConfig[tipoActual],
          {
            id: idNuevo,
            tipo: tipoActual,
            monto_min: currencyToNumber(values.monto_max, moneda) + 1,
            monto_max: null,
            origen: {
              nuevo: 0,
              representante: 0,
              volantecambiado: 0,
              importacion: 0
            }
          }
        ];
      }
    } else {
      tasaConfig[tipoActual][indexRangoModificado] = {
        ...tasaConfig[tipoActual][indexRangoModificado],
        ...values,
        monto_min: currencyToNumber(values.monto_min, moneda),
        monto_max: currencyToNumber(values.monto_max, moneda)
      };
    }

    form.setFieldsValue({ tasa_config: tasaConfig });
    setModalVisible(false);
  };

  const onFileSuccess = (data) => {
    let tasaConfig = form.getFieldValue('tasa_config');
    const formatedList = data.map((item, index) => ({
      id: index + 1,
      tipo: tipoActual,
      monto_min: item.monto_desde,
      monto_max: item.monto_hasta,
      origen: {
        nuevo: Number(item.representante_nuevo).toFixed(4),
        representante: Number(item.representante_usado).toFixed(4),
        volantecambiado: Number(item.volante_cambiado).toFixed(4),
        importacion: Number(item.importacion_directa).toFixed(4)
      }
    }));
    tasaConfig[tipoActual] = formatedList;
    tasaConfig = formatearListado(tasaConfig);
    form.setFieldsValue({ tasa_config: tasaConfig });
  };

  const onFinish = async (values) => {
    const coberturaToSend = { ...values };

    coberturaToSend.opciones = opcionesCobertura.map((item) => {
      const adapted = coberturaAdapter(
        item,
        monedas.find((e) => e.id === item.id_moneda),
        coberturasSistemaCore
      );
      return {
        ...adapted
      };
    });

    if (coberturaToSend.opciones && coberturaToSend.opciones.length === 0) {
      message.warning('Debe agregar al menos una opción de cobertura');
      return;
    }
    try {
      setGuardando(true);
      if (coberturaConfigurada) {
        await instanceRef.instance.put(
          `/cobertura/${coberturaConfigurada.id}`,
          coberturaToSend
        );
      } else {
        await instanceRef.instance.post(
          `/cobertura/${idAseguradoraProducto}`,
          coberturaToSend
        );
      }
      setGuardando(false);
      history.push(`/aseguradoraproducto/cobertura/${idAseguradoraProducto}`);
      message.success('Cobertura configurada correctamente');
    } catch (err) {
      errorHandler(err, 'Error al enviar');
      setGuardando(false);
    }
  };

  const onFinishOpcion = (values) => {
    const actualizado = { ...values };

    if (!actualizado.id) {
      actualizado.id = ultimoId;
      setUltimoId(ultimoId - 1);
    } else {
      actualizado.editado = true;
    }

    const opcionEditada = opcionesCobertura.find(
      (e) => actualizado.id && actualizado.id === e.id
    );

    if (!opcionEditada) {
      setOpcionesCobertura([...opcionesCobertura, actualizado]);
    } else {
      const copia = cloneDeep(opcionesCobertura);

      // Actualizar
      for (let i = 0; i < copia.length; i += 1) {
        if (copia[i].id === actualizado.id) {
          copia[i] = actualizado;
        }
      }
      setOpcionesCobertura([...copia]);
    }

    /* const idActualizado = modalOpcion.getFieldValue('id');
    if (idActualizado === undefined) {
      actualizado.id = idActualOpcion;
      setIdActualOpcion((prev) => prev - 1);
      setOpcionesCobertura((prevArray) => [...prevArray, actualizado]);
    } else {
      setOpcionesCobertura((prevArray) => {
        const index = prevArray.findIndex((item) => item.id === idActualizado);
        const existente = prevArray.find((item) => item.id === idActualizado);
        const copia = cloneDeep(prevArray);
        copia.splice(index, 1, { ...existente, ...actualizado });
        return copia;
      });
    } */

    setModalOpcionVisible(false);
  };

  const onAddOpcion = () => {
    setModalOpcionVisible(true);
  };
  const onEditOpcion = (values) => {
    const coberturaOpcionEdit = { ...values };

    if (values.tasa_config) {
      coberturaOpcionEdit.tasa_tipo = 'tasa_config';
      coberturaOpcionEdit.tasa_config = normalizeTasas(
        values.tasa_config.tasas
      );
    } else {
      coberturaOpcionEdit.tasa_config = configInicial;
    }
    if (values.tasa_pura) {
      coberturaOpcionEdit.tasa_tipo = 'tasa_pura';
    }
    coberturaOpcionEdit.codigo_cobertura = coberturaOpcionEdit.cobertura_config
      ? coberturaOpcionEdit.cobertura_config.codigo_cobertura
      : null;
    modalOpcion.setFieldsValue(coberturaOpcionEdit);
    setModalOpcionVisible(true);
  };

  const onDeleteOpcion = (index) => {
    setOpcionesCobertura((prevArray) => {
      const copia = cloneDeep(prevArray);
      copia.splice(index, 1);
      return copia;
    });
  };

  const normalizeTasas = (tasas) => {
    const tasasNormalizadas = {};

    tasas.forEach((item) => {
      if (tasasNormalizadas[item.tipo]) {
        tasasNormalizadas[item.tipo].push(item);
      } else {
        tasasNormalizadas[item.tipo] = [item];
      }
    });
    return tasasNormalizadas;
  };

  useEffect(() => {
    loadAseguradoraProducto();
    if (idCobertura) {
      loadModel();
    }
    loadCoberturasSebaot();
    loadGrupos();
    getMonedas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (form && monedas) {
      if (coberturaConfigurada) {
        const values = {
          descripcion: coberturaConfigurada.descripcion,
          id_grupo: `${coberturaConfigurada.grupo.id}`,
          subcoberturas: coberturaConfigurada.subcoberturas
          /* opcion: coberturaConfigurada.opcion,
          criterio: coberturaConfigurada.criterio,
          tasa_pura: coberturaConfigurada.tasa_pura,
          costo_min: coberturaConfigurada.costo_min,
          costo_max: coberturaConfigurada.costo_max,
          monto: coberturaConfigurada.monto,
          aplica_formula: coberturaConfigurada.aplica_formula,
          aplica_descuento: coberturaConfigurada.aplica_descuento,
          secundaria: coberturaConfigurada.secundaria,
          id_moneda: coberturaConfigurada.id_moneda,
          id_grupo: `${coberturaConfigurada.grupo.id}`,
          codigo_cobertura: coberturaConfigurada.cobertura_config
            ? coberturaConfigurada.cobertura_config.codigo_cobertura
            : null */
        };
        if (coberturaConfigurada.tasa_config) {
          values.tasa_tipo = 'tasa_config';
          values.tasa_config = normalizeTasas(
            coberturaConfigurada.tasa_config.tasas
          );
        } else {
          values.tasa_config = configInicial;
        }
        if (coberturaConfigurada.tasa_pura) {
          values.tasa_tipo = 'tasa_pura';
        }
        if (values.id_moneda) {
          setMoneda(
            monedas.find((item) => item.id === Number(values.id_moneda))
          );
        }
        form.setFieldsValue(values);
      } else if (Object.keys(form.getFieldValue()).length === 0) {
        const values = {
          tasa_config: configInicial
        };
        const monedaGuaranies = Object.values(monedas).find(
          (item) => item.codigo === 'gs'
        );
        if (monedaGuaranies) {
          setMoneda(monedaGuaranies);
        } else {
          const codigoPrimeraMoneda = Object.keys(monedas)[0];
          setMoneda(monedas[codigoPrimeraMoneda]);
        }
        form.setFieldsValue(values);
      }
    }
  }, [form, coberturaConfigurada, configInicial, monedas]);

  return (
    <ResponsiveContainer size="large">
      <Card title="Configuración de coberturas" style={{ textAlign: 'left' }}>
        <Spin spinning={loading}>
          <Form
            // {...formDialogLayout}
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="cobertura"
            autoComplete="off"
            layout="vertical"
          >
            {monedas && monedas.length > 0 && (
              <>
                {!formTasaVisible && (
                  <>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Descripción"
                          name="descripcion"
                          rules={[{ required: true }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Grupo de coberturas"
                          name="id_grupo"
                          rules={[{ required: true }]}
                        >
                          <Select
                            loading={loadingGrupos}
                            notFoundContent={
                              <NotFoundContentLoading loading={loadingGrupos} />
                            }
                            showSearch
                            allowClear
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                            optionFilterProp="children"
                            placeholder="Seleccionar grupo"
                          >
                            {grupos.map((item) => (
                              <Option key={`${item.id}`} value={`${item.id}`}>
                                {`${
                                  item.descripcion.trim() || '(Sin definir)'
                                }`}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        return (
                          <Row gutter={[16, 0]}>
                            <Col xs={24} md={24}>
                              <div style={{ paddingBottom: 20 }}>
                                <AseguradoraProductoCoberturaOpciones
                                  opciones={opcionesCobertura}
                                  monedas={monedas}
                                  loading={loading}
                                  onAdd={onAddOpcion}
                                  onEdit={onEditOpcion}
                                  onDelete={onDeleteOpcion}
                                  coberturasSistemaCore={coberturasSistemaCore}
                                />
                              </div>
                            </Col>

                            <Col xs={24} md={24}>
                              <Space>
                                <Button
                                  type="primary"
                                  htmlType="submit"
                                  loading={guardando}
                                  disabled={eliminando}
                                >
                                  {idCobertura ? 'Actualizar' : 'Agregar'}
                                </Button>
                                <Button
                                  disabled={guardando || eliminando}
                                  onClick={() => {
                                    history.push(
                                      `/aseguradoraproducto/cobertura/${idAseguradoraProducto}`
                                    );
                                  }}
                                >
                                  Cancelar
                                </Button>
                                {coberturaConfigurada && (
                                  <Popconfirm
                                    title="¿Está seguro? Se eliminará toda la información de la cobertura"
                                    onConfirm={confirmDelete}
                                    okText="Si"
                                    cancelText="No"
                                    st
                                  >
                                    <Button
                                      type="link"
                                      loading={eliminando}
                                      disabled={guardando}
                                      danger
                                    >
                                      Eliminar
                                    </Button>
                                  </Popconfirm>
                                )}
                              </Space>
                            </Col>
                          </Row>
                        );
                      }}
                    </Form.Item>
                  </>
                )}
                {formTasaVisible && (
                  <div>
                    <>
                      <Tabs
                        activeKey={tipoActual}
                        size="small"
                        onChange={(tab) => onChangeActiveTab(tab)}
                      >
                        {TIPOS_VEHICULO.map((item) => {
                          const columns = [
                            {
                              title: 'Nro',
                              dataIndex: ['id'],
                              key: ['id'],
                              align: 'right'
                            },
                            {
                              title: 'Desde',
                              dataIndex: ['monto_min'],
                              key: ['monto_min'],
                              align: 'right',
                              render: (value, record) => (
                                <Typography.Text
                                  type={
                                    errorMinimo(
                                      record,
                                      form.getFieldValue(['tasa_config', item]),
                                      moneda
                                    )
                                      ? 'danger'
                                      : undefined
                                  }
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  {formatoMonedaGenerico(moneda, value)}
                                </Typography.Text>
                              )
                            },
                            {
                              title: 'Hasta',
                              dataIndex: ['monto_max'],
                              key: ['monto_max'],
                              align: 'right',
                              render: (value, record, recordIndex) => (
                                <Typography.Text
                                  type={
                                    errorMaximo(
                                      record,
                                      form.getFieldValue(['tasa_config', item]),
                                      moneda
                                    )
                                      ? 'danger'
                                      : undefined
                                  }
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  {(value === null ||
                                    currencyToNumber(value, moneda) === 0) &&
                                  recordIndex ===
                                    form.getFieldValue(['tasa_config', item])
                                      .length -
                                      1
                                    ? '-'
                                    : formatoMonedaGenerico(moneda, value)}
                                </Typography.Text>
                              )
                            },
                            {
                              title: 'Nuevo',
                              dataIndex: ['origen', 'nuevo'],
                              key: ['origen', 'nuevo'],
                              align: 'right',
                              render: renderPorcentaje
                            },
                            {
                              title: 'Representante',
                              dataIndex: ['origen', 'representante'],
                              key: ['origen', 'representante'],
                              align: 'right',
                              render: renderPorcentaje
                            },
                            {
                              title: 'Volante cambiado',
                              dataIndex: ['origen', 'volantecambiado'],
                              key: ['origen', 'volantecambiado'],
                              align: 'right',
                              render: renderPorcentaje
                            },
                            {
                              title: 'Importación',
                              dataIndex: ['origen', 'importacion'],
                              key: ['origen', 'importacion'],
                              align: 'right',
                              render: renderPorcentaje
                            },
                            {
                              title: 'Acciones',
                              key: 'action',
                              width: '10px',
                              align: 'left',
                              render: (text, record, recordIndex) => {
                                return (
                                  <Space size="small">
                                    <Tooltip title="Configurar">
                                      <Button
                                        icon={<SettingOutlined />}
                                        type="text"
                                        size="middle"
                                        onClick={() => {
                                          setModalVisible(true);
                                          setIdActual(record.id);
                                          setInsertarIntermedio(false);
                                          modalForm.setFieldsValue(record);
                                        }}
                                      />
                                    </Tooltip>
                                    {recordIndex !==
                                      form.getFieldValue(['tasa_config', item])
                                        .length -
                                        1 && (
                                      <Tooltip title="Insertar rango debajo">
                                        <Button
                                          icon={<InsertRowBelowOutlined />}
                                          type="text"
                                          size="middle"
                                          onClick={() =>
                                            agregarFila(
                                              record,
                                              form.getFieldValue([
                                                'tasa_config',
                                                item
                                              ])
                                            )
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                  </Space>
                                );
                              }
                            }
                          ];
                          const data = form.getFieldValue([
                            'tasa_config',
                            item
                          ]);
                          return (
                            <TabPane
                              tab={
                                <span>
                                  {errorPorTipo(data, moneda) ? (
                                    <Tooltip title="Verificar rangos y porcentajes">
                                      <ExclamationCircleOutlined />
                                    </Tooltip>
                                  ) : (
                                    <CheckCircleOutlined
                                      style={{ color: green.primary }}
                                    />
                                  )}

                                  {item}
                                </span>
                              }
                              key={item}
                            >
                              <div
                                style={{
                                  marginBottom: 16
                                }}
                              >
                                <br />
                                <Space>
                                  <Upload
                                    showUploadList={false}
                                    customRequest={(e) =>
                                      uploadFile(e, onFileSuccess, setLoading)
                                    }
                                    accept={formatosAceptados}
                                  >
                                    <Button
                                      type="primary"
                                      ghost
                                      icon={<FileExcelOutlined />}
                                      disabled={loading}
                                    >
                                      Cargar planilla
                                    </Button>
                                  </Upload>
                                </Space>
                                <span
                                  style={{
                                    marginLeft: 8
                                  }}
                                >
                                  {hasSelected
                                    ? `Seleccionados ${selectedRowKeys[tipoActual].length} items`
                                    : ''}
                                </span>
                              </div>
                              <Table
                                scroll
                                rowSelection={rowSelection}
                                style={{ width: 'fit-content' }}
                                bordered
                                dataSource={[...data]}
                                columns={columns}
                                pagination={{
                                  pageSize: 50
                                }}
                                rowKey={(record) => record.id}
                                size="small"
                                locale={{
                                  emptyText: (
                                    <>
                                      <Empty
                                        className="ant-empty-small"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                      />
                                    </>
                                  )
                                }}
                              />
                            </TabPane>
                          );
                        })}
                      </Tabs>
                    </>

                    <br />
                    <Space>
                      <Button
                        type="primary"
                        htmlType="button"
                        onClick={() => onOk()}
                      >
                        Aceptar
                      </Button>
                      <Button htmlType="button" onClick={() => onCancel()}>
                        Cancelar
                      </Button>
                    </Space>
                  </div>
                )}
              </>
            )}
          </Form>
        </Spin>
      </Card>
      <Modal
        title="Configurar rango"
        width={700}
        open={modalVisible}
        afterClose={() => modalForm.resetFields()}
        onCancel={() => {
          setModalVisible(false);
        }}
        okButtonProps={{ form: 'tasa_config', htmlType: 'submit' }}
      >
        <Form
          form={modalForm}
          onFinish={onFinishRango}
          scrollToFirstError
          name="tasa_config"
          id="tasa_config"
          autoComplete="off"
          layout="vertical"
        >
          <Row gutter={8}>
            <Col md={12}>
              <Form.Item
                label="Desde"
                name={['monto_min']}
                rules={[{ required: true }]}
              >
                <CurrencyInputCustom
                  readOnly={idActual === 1 && !insertarIntermedio}
                  moneda={moneda}
                  bordered
                  style={{ height: 35.14 }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                label="Hasta"
                name={['monto_max']}
                dependencies={['monto_min']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const montoMin = getFieldValue('monto_min');
                      const montoMax = value;
                      if (!montoMax || !montoMin) {
                        return Promise.resolve();
                      }
                      const tasaConfig = form.getFieldValue('tasa_config');
                      if (idActual === tasaConfig[tipoActual].length) {
                        return Promise.resolve();
                      }
                      if (
                        currencyToNumber(montoMin, moneda) >
                        currencyToNumber(montoMax, moneda)
                      ) {
                        return Promise.reject(new Error('Verificar rangos'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <CurrencyInputCustom
                  moneda={moneda}
                  bordered
                  style={{ height: 35.14 }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                label="Nuevo"
                name={['origen', 'nuevo']}
                rules={[{ required: true }]}
                normalize={normalizePorcentaje}
              >
                <Input
                  style={{
                    width: 200
                  }}
                  placeholder="0"
                  min={0}
                  max={100}
                  step="any"
                  type="number"
                  suffix="%"
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                label="Representante"
                name={['origen', 'representante']}
                rules={[{ required: true }]}
                normalize={normalizePorcentaje}
              >
                <Input
                  style={{
                    width: 200
                  }}
                  placeholder="0"
                  min={0}
                  max={100}
                  step="any"
                  type="number"
                  suffix="%"
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                label="Volante cambiado"
                name={['origen', 'volantecambiado']}
                rules={[{ required: true }]}
                normalize={normalizePorcentaje}
              >
                <Input
                  style={{
                    width: 200
                  }}
                  placeholder="0"
                  min={0}
                  max={100}
                  step="any"
                  type="number"
                  suffix="%"
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                label="Importación"
                name={['origen', 'importacion']}
                rules={[{ required: true }]}
                normalize={normalizePorcentaje}
              >
                <Input
                  style={{
                    width: 200
                  }}
                  placeholder="0"
                  min={0}
                  max={100}
                  step="any"
                  type="number"
                  suffix="%"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        title="Configurar opción"
        width={700}
        open={modalOpcionVisible}
        afterClose={() => modalOpcion.resetFields()}
        onCancel={() => {
          setModalOpcionVisible(false);
        }}
        okButtonProps={{ form: 'form_opcion', htmlType: 'submit' }}
      >
        <CoberturaOpcionForm
          form={modalOpcion}
          formName="form_opcion"
          moneda={moneda}
          monedaList={monedas}
          aseguradoraProducto={aseguradoraProducto}
          coberturasSistemaCore={coberturasSistemaCore}
          loadingCobertura={loadingCobertura}
          onFinish={onFinishOpcion}
        />
      </Modal>
    </ResponsiveContainer>
  );
};

export default AseguradoraProductoCoberturaForm;
